import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Truck, Calculator, MapPin, Users, FileText, Clock, Shield, Zap, 
  ChevronRight, Check, Star, ArrowRight, Package, Route, Brain, 
  Globe, CreditCard, Sparkles, Camera, DollarSign, AlertTriangle, 
  TrendingUp, CheckCircle, Menu, X, MessageSquare, Receipt, Anchor,
  BarChart3, Bell, Store, Ship, FileCheck, Wrench, Timer, Briefcase,
  Building2
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Feature outcomes
  const outcomes = [
    {
      outcome: 'Get paid faster',
      feature: 'AI rate parsing + automated invoice data',
      benefit: 'Reduce billing errors & speed up payments',
      icon: DollarSign,
      color: 'text-green-400'
    },
    {
      outcome: 'Reduce errors',
      feature: 'Auto-attached photos & OCR',
      benefit: 'Avoid disputes',
      icon: CheckCircle,
      color: 'text-blue-400'
    },
    {
      outcome: 'Avoid delays',
      feature: 'Port holds & LFD alerts',
      benefit: 'Prevent costly demurrage',
      icon: Bell,
      color: 'text-amber-400'
    },
    {
      outcome: 'Increase efficiency',
      feature: 'Real-time GPS + driver messaging',
      benefit: 'Faster operations',
      icon: Route,
      color: 'text-purple-400'
    },
    {
      outcome: 'Simplify accounting',
      feature: 'QuickBooks sync + payroll module',
      benefit: 'All-in-one back office',
      icon: Calculator,
      color: 'text-cyan-400'
    }
  ];

  // Core benefits
  const benefits = [
    {
      icon: Brain,
      title: 'AI-Driven Document Parsing',
      description: 'No typing. Upload BOLs, rate sheets, PODs — AI extracts and attaches them to the correct load.',
      color: 'text-purple-400',
      bg: 'bg-purple-500/10'
    },
    {
      icon: MessageSquare,
      title: 'Smart Dispatch & Driver Communication',
      description: 'Send jobs, photos, and instructions instantly via in-app messaging with real-time GPS.',
      color: 'text-green-400',
      bg: 'bg-green-500/10'
    },
    {
      icon: Anchor,
      title: 'Port & Container Intelligence',
      description: 'Live updates on LFD, holds, and container availability — alerts before delays occur.',
      color: 'text-blue-400',
      bg: 'bg-blue-500/10'
    },
    {
      icon: Receipt,
      title: 'Integrated Billing & Payroll',
      description: 'QuickBooks sync, automated trip pay, owner-operator settlement, and expense tracking all in one place.',
      color: 'text-amber-400',
      bg: 'bg-amber-500/10'
    },
    // iter 258 — newer modules added to landing showcase
    {
      icon: Ship,
      title: 'Empty Receiving Schedules (PIP)',
      description: 'See which POLA/POLB terminals are accepting your empties — by SSL, by container type, refreshed 4x/day.',
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      badge: '73% less than BlueCargo'
    },
    {
      icon: FileCheck,
      title: 'D&D Dispute Dashboard',
      description: 'Track every demurrage & detention charge, auto-fill closure evidence, and generate PDF dispute letters in one click.',
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      badge: 'Recover thousands/mo'
    },
    {
      icon: Shield,
      title: 'Equipment & DOT Compliance',
      description: '31 inspection types pre-configured, recurring schedules, overdue alerts — stay inspection-ready year-round.',
      color: 'text-amber-400',
      bg: 'bg-amber-500/10'
    },
    {
      icon: Wrench,
      title: 'Roadside Rescue',
      description: 'GPS-matched mechanics & tow when your truck breaks down. In-app payments, real-time tracking, full incident history.',
      color: 'text-red-400',
      bg: 'bg-red-500/10'
    },
    {
      icon: Timer,
      title: 'TimeClock Pro',
      description: 'Geofenced clock-in/clock-out for office staff and yard crews. Payroll-ready CSV exports, multi-shift support.',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10'
    },
    {
      icon: Briefcase,
      title: 'Broker TMS',
      description: 'Full freight brokerage: carrier network, DAT/Truckstop integration, AI rate-con parsing, carrier bidding & settlements.',
      color: 'text-rose-400',
      bg: 'bg-rose-500/10'
    },
    {
      icon: Truck,
      title: 'OTR + HOS Compliance',
      description: 'Internal load board, real-time HOS/ELD monitoring, automatic violation alerts, and AI load matching.',
      color: 'text-blue-400',
      bg: 'bg-blue-500/10'
    },
    {
      icon: Store,
      title: 'Equipment & Parking Marketplace',
      description: 'Find chassis, trailers, mechanics, and yard parking instantly. Built-in payments, no broker fees.',
      color: 'text-indigo-400',
      bg: 'bg-indigo-500/10',
      badge: 'Free with every plan'
    }
  ];

  // Competitors
  const competitors = [
    {
      type: 'Traditional TMS',
      examples: 'Axon, ITS Dispatch, Loadstop',
      advantage: 'Quick onboarding, modern UX, automated QuickBooks sync'
    },
    {
      type: 'Free/Low-Cost TMS',
      examples: 'AscendTMS',
      advantage: 'Enterprise-level automation at small-fleet pricing'
    },
    {
      type: 'GPS/ELD Focused',
      examples: 'Motive (KeepTruckin)',
      advantage: 'End-to-end operations + billing automation'
    },
    {
      type: 'Enterprise TMS',
      examples: 'PCS, Trimble, McLeod',
      advantage: 'Powerful yet simple for small-mid fleets'
    }
  ];

  // Testimonials
  const testimonials = [
    {
      quote: "Drayly cut our admin time in half and saved $3,200 in demurrage last month.",
      author: "Verified User",
      role: "Fleet Owner"
    },
    {
      quote: "Our billing disputes dropped 35% within the first month.",
      author: "Fleet Manager",
      role: "42 Trucks"
    }
  ];

  // Key metrics
  const keyMetrics = [
    { value: '45+', unit: 'hrs/week', label: 'saved per fleet' },
    { value: '$12k+', unit: '', label: 'demurrage avoided' },
    { value: '27%', unit: '', label: 'fewer billing errors' },
    { value: '99%', unit: '', label: 'uptime' }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo192.png" alt="Drayly" className="w-12 h-12 rounded-xl object-contain" />
              <span className="font-bold text-xl text-white">Drayly</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#outcomes" className="text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="#roi" className="text-slate-400 hover:text-white transition-colors">ROI Calculator</a>
              <Link to="/pricing" className="text-slate-400 hover:text-white transition-colors">Pricing</Link>
              <Link to="/marketplace" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                <Store className="h-4 w-4" />
                Marketplace
              </Link>
              <Link to="/login" className="text-slate-400 hover:text-white transition-colors">Sign In</Link>
              <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
                Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <Button variant="ghost" className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-800">
              <div className="flex flex-col gap-4">
                <a href="#outcomes" className="text-slate-400 hover:text-white px-2">Features</a>
                <a href="#roi" className="text-slate-400 hover:text-white px-2">ROI Calculator</a>
                <Link to="/pricing" className="text-slate-400 hover:text-white px-2">Pricing</Link>
                <Link to="/marketplace" className="text-slate-400 hover:text-white px-2 flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Marketplace
                </Link>
                <Link to="/login" className="text-slate-400 hover:text-white px-2">Sign In</Link>
                <Button onClick={() => navigate('/register')} className="bg-blue-600">Start Free Trial</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* 1️⃣ Hero Section */}
      <section className="pt-28 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 via-purple-600/5 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="mb-6 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 border-blue-500/30 px-4 py-1.5">
                <Sparkles className="h-4 w-4 mr-2" />
                AI-Powered Operations Platform
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                AI-Powered Operations Platform for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Trucking & Freight
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-400 mb-8 max-w-2xl lg:mx-0 mx-auto">
                Stop wasting hours on paperwork and demurrage fees — automate dispatch,
                document parsing, port intelligence, and billing in one platform. Now
                with dedicated <span className="text-white font-semibold">Broker TMS</span> and
                <span className="text-white font-semibold"> Moving Company</span> modules —
                one operations hub for every side of your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" onClick={() => navigate('/register')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-lg px-8 py-6 shadow-lg shadow-blue-500/25">
                  Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" 
                  onClick={() => document.getElementById('roi').scrollIntoView({ behavior: 'smooth' })}
                  className="border-slate-700 text-white hover:bg-slate-800 text-lg px-8 py-6">
                  <Calculator className="mr-2 h-5 w-5" />
                  See Your ROI
                </Button>
              </div>
            </div>
            
            {/* Dashboard Preview */}
            <div className="relative">
              <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl overflow-hidden">
                <div className="bg-slate-800 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <span className="text-xs text-slate-400 ml-2">Drayly Dashboard</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <Brain className="h-5 w-5 text-purple-400" />
                    <div className="flex-1">
                      <p className="text-sm text-white">AI Document Parsing</p>
                      <p className="text-xs text-slate-400">3 BOLs processed • Rate data extracted</p>
                    </div>
                    <Badge className="bg-purple-500/20 text-purple-300 border-none text-xs">Auto</Badge>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-400" />
                    <div className="flex-1">
                      <p className="text-sm text-white">Port Alert: LFD Tomorrow</p>
                      <p className="text-xs text-slate-400">MSCU1234567 at LBCT</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-300 border-none text-xs">Urgent</Badge>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <MapPin className="h-5 w-5 text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm text-white">Live Driver Tracking</p>
                      <p className="text-xs text-slate-400">12 drivers active • 8 en route</p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-300 border-none text-xs">Live</Badge>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-pulse">
                Live Updates
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2️⃣ Key Metrics Section */}
      <section className="py-12 px-4 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {keyMetrics.map((metric, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                  {metric.value}<span className="text-blue-400">{metric.unit}</span>
                </div>
                <div className="text-slate-500 text-sm">{metric.label}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* 3️⃣ Outcomes Section */}
      <section id="outcomes" className="py-20 px-4 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Stop Wasting Hours on Paperwork
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Drayly replaces spreadsheets, WhatsApp threads, and disconnected tools with a smart, 
              AI-driven platform — so your team can focus on revenue-generating operations.
            </p>
          </div>
          
          {/* Outcome Table */}
          <div className="rounded-xl border border-slate-800 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800/50">
                  <th className="text-left p-4 text-slate-400 font-medium">Outcome</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">How Drayly Helps</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Benefit</th>
                </tr>
              </thead>
              <tbody>
                {outcomes.map((item, idx) => (
                  <tr key={idx} className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <item.icon className={`h-5 w-5 ${item.color}`} />
                        <span className="text-white font-medium">{item.outcome}</span>
                      </div>
                    </td>
                    <td className="p-4 text-slate-400 hidden sm:table-cell">{item.feature}</td>
                    <td className="p-4 text-slate-300">{item.benefit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="text-center">
            <Button size="lg" onClick={() => document.getElementById('roi').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              See Your ROI <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 4️⃣ ROI Calculator - Links to Pricing Page */}
      <section id="roi" className="py-20 px-4 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
            <Calculator className="h-4 w-4 mr-2" />
            ROI Calculator
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            See How Drayly Pays for Itself
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Calculate your potential savings from reduced demurrage, admin time savings, and HOS violation prevention.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-6 text-center">
                <DollarSign className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-400">50%</p>
                <p className="text-slate-400 text-sm">Demurrage Reduction</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="p-6 text-center">
                <Clock className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-400">20+ hrs</p>
                <p className="text-slate-400 text-sm">Admin Time Saved/Week</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-6 text-center">
                <Shield className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-400">$1000+</p>
                <p className="text-slate-400 text-sm">Per HOS Violation Avoided</p>
              </CardContent>
            </Card>
          </div>
          
          <Button size="lg" onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500">
            Calculate Your Exact Savings <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* 5️⃣ Core Features */}
      <section className="py-20 px-4 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to Run Smarter
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="relative bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all" data-testid={`landing-feature-${idx}`}>
                {benefit.badge && (
                  <div className="absolute -top-2 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-amber-500 text-slate-900 shadow-md">
                    {benefit.badge}
                  </div>
                )}
                <CardContent className="p-6">
                  <div className={`inline-flex p-3 rounded-xl ${benefit.bg} mb-4`}>
                    <benefit.icon className={`h-6 w-6 ${benefit.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button size="lg" onClick={() => document.getElementById('roi').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              See Your ROI <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 6️⃣ Social Proof */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-2xl font-bold text-white mb-1">45+ hrs</p>
              <p className="text-sm text-slate-400">saved per week</p>
            </div>
            <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-2xl font-bold text-white mb-1">27%</p>
              <p className="text-sm text-slate-400">fewer billing errors</p>
            </div>
            <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-2xl font-bold text-white mb-1">$12k+</p>
              <p className="text-sm text-slate-400">demurrage avoided</p>
            </div>
            <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-2xl font-bold text-white mb-1">99%</p>
              <p className="text-sm text-slate-400">uptime</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, idx) => (
              <Card key={idx} className="bg-slate-800/30 border-slate-700">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-lg text-white mb-6 italic">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {t.author[0]}
                    </div>
                    <div>
                      <p className="text-white font-medium">{t.author}</p>
                      <p className="text-sm text-slate-400">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Button size="lg" onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 7️⃣ Competitive Differentiation */}
      <section className="py-20 px-4 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">Why Drayly</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">The Drayly Advantage</h2>
            <p className="text-lg text-slate-400">AI automation + port intelligence + outcomes-first approach</p>
          </div>
          
          <div className="rounded-xl border border-slate-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800/50">
                  <th className="text-left p-4 text-slate-400 font-medium">Competitor Type</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Drayly Advantage</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c, idx) => (
                  <tr key={idx} className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors">
                    <td className="p-4">
                      <p className="text-white font-medium">{c.type}</p>
                      <p className="text-xs text-slate-500">{c.examples}</p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                        <span className="text-slate-300">{c.advantage}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="text-center mt-10">
            <Button size="lg" onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 7.a Broker TMS Module Deep Dive — iter 294.9 */}
      <section id="broker-module" className="py-20 px-4 bg-gradient-to-b from-slate-950 to-slate-900 border-y border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-1.5">
              <Building2 className="h-4 w-4 mr-2" /> Broker TMS Module
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Freight brokerage without the spreadsheet chaos
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Post loads to DAT & Truckstop, run carrier packet + RMIS in one flow,
              handle TL <em>and</em> LTL from the same board, settle carriers in one click.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Truck className="w-5 h-5 text-blue-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Post & Cover Loads</h3>
                </div>
                <p className="text-sm text-slate-400">Sync loads to DAT / Truckstop with one toggle. Track bids side-by-side, accept the best, generate the rate confirmation instantly.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Carrier Packet + RMIS</h3>
                </div>
                <p className="text-sm text-slate-400">Onboard carriers with a public link. Auto-check MC/DOT, insurance, W-9, and RMIS status. No more chasing PDFs.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"><FileText className="w-5 h-5 text-amber-400" /></div>
                  <h3 className="text-lg font-semibold text-white">TL & LTL, one board</h3>
                </div>
                <p className="text-sm text-slate-400">Full truckload and LTL live on the same load board with mode-aware filters — no separate tools, no duplicated data.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center"><CreditCard className="w-5 h-5 text-purple-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Settle in one click</h3>
                </div>
                <p className="text-sm text-slate-400">Auto-match POD to load, calculate net after quick-pay, cut carrier checks or ACH — QuickBooks synced in real time.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 7.b Moving Module Deep Dive — iter 294.9 */}
      <section id="moving-module" className="py-20 px-4 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-1.5">
              <Truck className="h-4 w-4 mr-2" /> Moving Company Module
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for movers, closings and foremen in the field
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Digital Closing sheets with AI photo parsing, foreman mobile view with
              signature capture, and one-click Bill of Lading packets with every photo stitched in.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center"><Camera className="w-5 h-5 text-purple-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Snap-to-Closing (AI Vision)</h3>
                </div>
                <p className="text-sm text-slate-400">Driver photographs the paper Closing sheet — Gemini vision parses every row (customer, CF, rate, collected, payment) into the electronic version. No retyping handwritten forms.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Collect, confirm, settle</h3>
                </div>
                <p className="text-sm text-slate-400">Per-line "Confirm delivery date", "Collect balance + payment method", and "Add extras (Shuttle / Stairs / Long Carry)". Auto-computed carrier balance, DUE, and end-of-trip settlement.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><FileText className="w-5 h-5 text-blue-400" /></div>
                  <h3 className="text-lg font-semibold text-white">BOL Packet + Foreman Mobile</h3>
                </div>
                <p className="text-sm text-slate-400">Foreman captures signatures + before/after/damage photos on any phone. One click stitches customer info, dates, signatures, and every photo into a printable BOL packet — ready for insurance claims.</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center"><MapPin className="w-5 h-5 text-indigo-400" /></div>
                  <h3 className="text-lg font-semibold text-white">Warehouse & Last-Mile Handoff</h3>
                </div>
                <p className="text-sm text-slate-400">Dropped at your TX / AZ / CA (or custom) warehouse? One tap flips the line to "In Warehouse". Assign the last-mile driver and track who actually completes each customer.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 8️⃣ Pricing Section - Links to Full Pricing Page */}
      <section id="pricing" className="py-20 px-4 bg-slate-900">        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
              Modular Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Flexible Pricing Built for Real Fleets
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Start with the Core Platform. Add only the operational modules your fleet actually needs.
            </p>
          </div>
          
          {/* Quick Pricing Overview */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Core Platform */}
            <Card className="bg-gradient-to-br from-slate-800/50 to-blue-950/30 border-blue-500/30">
              <CardContent className="p-6">
                <Badge className="mb-3 bg-blue-500 text-white border-0">Required Base</Badge>
                <h3 className="text-xl font-bold text-white mb-2">Core Platform</h3>
                <p className="text-slate-400 text-sm mb-4">Dashboard, messaging, tracking, invoicing & more</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">$179</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <p className="text-green-400 text-sm mt-1">Save 17% with annual billing</p>
              </CardContent>
            </Card>
            
            {/* Modules Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <Badge className="mb-3 bg-purple-500/20 text-purple-400 border-purple-500/30">Add-On Modules</Badge>
                <h3 className="text-xl font-bold text-white mb-2">Operational Modules</h3>
                <p className="text-slate-400 text-sm mb-4">Only pay for what you need</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">OTR + HOS Compliance</span>
                    <span className="text-slate-400">$199/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Port Intelligence</span>
                    <span className="text-slate-400">$149/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Accounting & Settlements</span>
                    <span className="text-slate-400">$129/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">AI Automation</span>
                    <span className="text-slate-400">$99/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Equipment Compliance</span>
                    <span className="text-slate-400">$99/mo</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Popular Stacks */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { name: 'Owner Operator', price: '$308', desc: 'Core + Accounting' },
              { name: 'Drayage Specialist', price: '$457', desc: 'Core + Port + Accounting' },
              { name: 'Scaling Fleet', price: '$755', desc: 'Most Popular', highlight: true },
              { name: 'Enterprise', price: '$854', desc: 'All Modules' }
            ].map((stack, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${stack.highlight ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 bg-slate-800/30'}`}>
                <p className="text-white font-medium">{stack.name}</p>
                <p className="text-2xl font-bold text-white">{stack.price}<span className="text-sm text-slate-400">/mo</span></p>
                <p className="text-xs text-slate-400">{stack.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Button size="lg" onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              View Full Pricing & Build Your Stack <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-slate-500 text-sm mt-4">14-day free trial • No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* 🔟 Final CTA */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10 pointer-events-none" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Work Smarter. Save Time.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Grow Profitably.
            </span>
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            See how much your fleet could save each month with AI automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-lg px-10 py-6 shadow-lg shadow-blue-500/25">
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo192.png" alt="Drayly" className="w-12 h-12 rounded-xl object-contain" />
                <span className="font-bold text-xl text-white">Drayly</span>
              </div>
              <p className="text-slate-400 text-sm">AI-Powered Freight Operations Platform</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#outcomes" className="text-slate-400 hover:text-white">Features</a></li>
                <li><Link to="/pricing" className="text-slate-400 hover:text-white">Pricing</Link></li>
                <li><a href="#roi" className="text-slate-400 hover:text-white">ROI Calculator</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-slate-400 hover:text-white" data-testid="footer-about-link">About</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white" data-testid="footer-contact-link">Contact</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-white" data-testid="footer-privacy-link">Privacy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white" data-testid="footer-terms-link">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Get Started</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register" className="text-slate-400 hover:text-white">Free Trial</Link></li>
                <li><Link to="/login" className="text-slate-400 hover:text-white">Sign In</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} Drayly. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
